import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useDispatch, useSelector } from 'react-redux';
import { signIn, selectAuthError, selectAuthLoading } from '../../store/slices/authSlice';
import { AppDispatch } from '../../store';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Container, Box, Typography, TextField, Button, Alert, Link, CircularProgress, Divider } from '@mui/material';
import GoogleAuthButton from '../../components/GoogleAuthButton';

const loginSchema = z.object({
    email: z.string().email({ message: 'Invalid email address' }),
    password: z.string().min(1, { message: 'Password is required' }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const authError = useSelector(selectAuthError);
    const isLoading = useSelector(selectAuthLoading);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
    });

    const onSubmit = async (data: LoginFormValues) => {
        const result = await dispatch(signIn(data));
        if (signIn.fulfilled.match(result)) {
            navigate('/');
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box
                    component="img"
                    src="/img/logo.png"
                    alt="Smart Nyuki Logo"
                    sx={{
                        width: 120,
                        height: 'auto',
                        mb: 2,
                    }}
                />
                <Typography component="h1" variant="h5">
                    Login
                </Typography>
                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        autoComplete="email"
                        autoFocus
                        {...register('email')}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        label="Password"
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        {...register('password')}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                    />
                    {authError && <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{authError}</Alert>}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Login'}
                    </Button>
                    <Box sx={{ width: '100%', textAlign: 'right', mb: 1 }}>
                        <Link component={RouterLink} to="/auth/forgot-password" variant="body2">
                            Forgot your password?
                        </Link>
                    </Box>
                    <Divider sx={{ my: 2 }}>OR</Divider>
                    <GoogleAuthButton
                        onSuccess={() => navigate('/')}
                        onError={(err) => console.error(err)}
                    />
                    <Link component={RouterLink} to="/auth/sign-up" variant="body2">
                        {"Don't have an account? Sign Up"}
                    </Link>
                </Box>
            </Box>
        </Container>
    );
};

export default LoginPage;
